import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Download, ExternalLink, FileText } from "lucide-react";
import { FunctionCall } from "components/hooks/useTaskAgentState";
import { ImageURLDialog } from "components/common/AttachmentDialog";
import LinkRouter from "components/navigation/LinkRouter";

const FILE_OUTPUT_TOOL_NAMES = new Set([
  "code_execution",
  "generate_document_image",
  "generate_image",
  "edit_image",
]);

const toPlainText = (value: unknown) => {
  const text = String(value ?? "");
  if (!/<\/?[a-z][\s\S]*>/i.test(text)) return text;

  const parser = new DOMParser();
  return parser.parseFromString(text, "text/html").body.textContent || "";
};

const fileTypeLabel = (contentType: string) => {
  if (
    contentType ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "Word document";
  }
  if (contentType === "application/pdf") return "PDF";
  if (contentType === "text/csv") return "CSV";
  return contentType || "File";
};

const documentOutputFromCall = (call: FunctionCall) => {
  const result = toPlainText(call.result);
  const urlMatch = result.match(
    /(?:https?:\/\/[^\s<]+)?\/documents\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
  );
  if (!urlMatch) return null;

  const quotedValues = Array.from(
    result.matchAll(/"([^"]+)"/g),
    (match) => match[1],
  );
  return {
    uuid: urlMatch[1],
    title: toPlainText(call.args.title) || quotedValues.at(-1) || "Document",
    action: /^created\b/i.test(result) ? "created" : "updated",
    isAdvisorPrivate: /\(private to advisors\)/i.test(result),
  };
};

export const ToolOutputsSection = ({
  functionCalls,
  includeDocuments = true,
  includeAllAttachments = false,
  compact = false,
}: {
  functionCalls: FunctionCall[];
  includeDocuments?: boolean;
  includeAllAttachments?: boolean;
  compact?: boolean;
}) => {
  const [openImageUrl, setOpenImageUrl] = useState("");
  const documentOutputs = includeDocuments
    ? Array.from(
        new Map(
          functionCalls
            .flatMap((call) => {
              const document = documentOutputFromCall(call);
              return document ? [document] : [];
            })
            .map((document) => [document.uuid, document]),
        ).values(),
      )
    : [];
  const attachmentOutputs = functionCalls.flatMap((call) =>
    (call.attachments || [])
      .filter(
        (attachment) =>
          includeAllAttachments ||
          attachment.content_type.startsWith("image/") ||
          FILE_OUTPUT_TOOL_NAMES.has(call.name),
      )
      .map((attachment) => ({ callId: call.id, attachment })),
  );

  if (documentOutputs.length === 0 && attachmentOutputs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginTop: compact ? "8px" : "12px",
      }}
    >
      {documentOutputs.map((document) => (
        <LinkRouter
          key={document.uuid}
          to={`/documents/${encodeURIComponent(document.uuid)}`}
          sx={{ padding: 0 }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: "12px",
              border: `1px solid ${theme.palette.gray[4]}`,
              borderRadius: "8px",
              padding: "12px 14px",
              transition: "background-color 0.2s ease-in-out",
              "&:hover": { backgroundColor: theme.palette.gray[1] },
            })}
          >
            <FileText size={20} color="#198282" />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body"
                color="text.primary"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {toPlainText(document.title) || "Untitled document"}
              </Typography>
              <Typography variant="bodySmall" color="text.secondary">
                {document.action === "created"
                  ? "Created document"
                  : "Updated document"}
                {document.isAdvisorPrivate &&
                  " · Private to advisors — share with the family from the document"}
              </Typography>
            </Box>
            <ExternalLink size={16} color="#8E9598" />
          </Box>
        </LinkRouter>
      ))}

      {attachmentOutputs.map(({ callId, attachment }, index) =>
        attachment.content_type.startsWith("image/") ? (
          <Box
            key={`${callId}-${attachment.url}-${index}`}
            sx={{ minWidth: 0 }}
          >
            <img
              src={attachment.url}
              alt={attachment.filename || "Generated image"}
              onClick={() => setOpenImageUrl(attachment.url)}
              style={{
                display: "block",
                maxHeight: compact ? "160px" : "480px",
                maxWidth: compact ? "240px" : "100%",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            />
            {attachment.filename && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ display: "block", marginTop: "4px" }}
              >
                {toPlainText(attachment.filename)}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            key={`${callId}-${attachment.url}-${index}`}
            component="a"
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: "inherit",
              textDecoration: "none",
              border: `1px solid ${theme.palette.gray[4]}`,
              borderRadius: "8px",
              padding: compact ? "8px 10px" : "12px 14px",
              transition: "background-color 0.2s ease-in-out",
              "&:hover": { backgroundColor: theme.palette.gray[1] },
            })}
          >
            <FileText size={20} color="#198282" />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body"
                color="text.primary"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {toPlainText(attachment.filename) || "Generated file"}
              </Typography>
              <Typography variant="bodySmall" color="text.secondary">
                {fileTypeLabel(attachment.content_type)}
              </Typography>
            </Box>
            <Download size={16} color="#8E9598" />
          </Box>
        ),
      )}

      {openImageUrl && (
        <ImageURLDialog
          attachmentUrl={openImageUrl}
          onClose={() => setOpenImageUrl("")}
        />
      )}
    </Box>
  );
};
